import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Button, Group as ButtonGroup, Menu, Modal, Table } from "@mantine/core";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  addLoading,
  setPaysheetVisibilityStatus,
} from "../../../../data/actions/appointmentActions";
import {
  removeShiftVenueSupervisor,
  rosterShiftVenueSupervisor,
} from "../../../../data/actions/shiftActions";
import { IState } from "../../../../data/reducers/state";
import {
  IAppointment,
  IAppointmentDetail,
  ISupervisorAppointmentDetail,
} from "../../../../types/appointments/Appointment";
import { AppointmentStatus } from "../../../../types/appointments/AppointmentStatus";
import { BonusType } from "../../../../types/payrates/bonusType";
import { Role } from "../../../../types/persons/Role";
import { IPerson } from "../../../../types/persons/person";
import {
  IShift,
  IShiftSession,
  IShiftSupervisor,
} from "../../../../types/shifts/Shift";
import { IVenue } from "../../../../types/venue/Venue";
import { toDateTime, toDateTimeNullable } from "../../../../util/date+helper";
import AppointmentView from "../../../appointments/AppointmentView";
import { CenteredLoader } from "../../../common/loader";
import TimeSelector from "../../../common/selectors/TimeSelector";
import AddLoadingView from "../AddLoadingView";
import { OfficialsSelector } from "../OfficialsSelector";

export interface IRosterSupervisorProps {
  shiftId: string;
  sessionId: string;
  venueId: string;

  shift?: IShift;
  session?: IShiftSession | null;

  showHideAppointmentFromSheetButton: boolean;

  personsMap?: { [personId: string]: IPerson };
  appointmentsMap?: {
    [appointmentId: string]: IAppointment<IAppointmentDetail>;
  };
  venueMap: { [venueId: string]: IVenue };
  actions?: {
    // definitely filled, but optional for the ActivitiesPageProps extension
    setPaysheetVisibilityStatus: (
      appointmentId: string,
      show: boolean,
    ) => Promise<any>;
    rosterShiftVenueSupervisor: (
      sessionId: string,
      venueId: string,
      person: string,
      start: Date,
      finish: Date,
    ) => Promise<IShift>;
    removeShiftVenueSupervisor: (
      sessionId: string,
      venueId: string,
      appointmentId: string,
    ) => Promise<IShift>;

    addLoading: (appoinmentId: string, loading: any) => Promise<any>;
  };
}

export interface RosterSupervisorState {
  editingRow?: number | null;
  toPersist?: {
    person: string | null;
    start: string | DateTime;
    finish: string | DateTime;
    comment?: string;
  } | null;
  editDraft?: {
    person: string | null;
    start: string | DateTime;
    finish: string | DateTime;
    comment?: string;
  } | null;
  editingAppointmentId?: string | null;
  editingOriginal?: {
    person: string | null;
    start: string | DateTime;
    finish: string | DateTime;
    comment?: string;
  } | null;
  persistSaving?: boolean;
  error?: string | null;
  openActionsRow?: number | null;
  addLoadingModalAppointment?: string | null;
  showAppointmentDetailsModalAppointment?: string | null;
  supervisorDefaultTimesMenuOpen?: boolean;
}

class RosterSupervisor extends React.PureComponent<
  IRosterSupervisorProps,
  RosterSupervisorState
> {
  constructor(props, context) {
    super(props, context);

    this.state = {};

    this.getRow = this.getRow.bind(this);
    this.addSupervisor = this.addSupervisor.bind(this);
    this.persist = this.persist.bind(this);
    this.persistEdit = this.persistEdit.bind(this);
    this.modifySupervisor = this.modifySupervisor.bind(this);
    this.modifyEditSupervisor = this.modifyEditSupervisor.bind(this);
    this.startEdit = this.startEdit.bind(this);
    this.cancelEdit = this.cancelEdit.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.toggleAddLoading = this.toggleAddLoading.bind(this);
    this.toggleShowAppointment = this.toggleShowAppointment.bind(this);
    this.toggleSupervisorDefaultTimesMenu = this.toggleSupervisorDefaultTimesMenu.bind(this);
  }

  venueTz() {
    return this.props.venueMap?.[this.props.venueId]?.tz;
  }

  correctedTz(date: DateTime) {
    const venueTz = this.venueTz();
    // just leave as current computer timzone
    if (!venueTz) return date
    return date.setZone(venueTz);
  }

  addSupervisor(start?: DateTime, finish?: DateTime) {
    let guessedStartDate: DateTime | undefined = start;
    let guessedFinishDate: DateTime | undefined = finish;

    const { session } = this.props;
    if (session) {
      const vid = this.props.venueId;
      if (!guessedFinishDate) {
        guessedFinishDate = toDateTime(session.finish);
      }
      if (!guessedStartDate) {
        if (
          !(
            session.supervisors &&
            vid in session.supervisors &&
            session.supervisors[vid].length > 0
          )
        ) {
          guessedStartDate = toDateTime(session.start);
        } else {
          const venueSupervisors = session.supervisors[this.props.venueId];
          const supervisor = venueSupervisors[venueSupervisors.length - 1];
          if (supervisor && this.props.appointmentsMap) {
            const appointment = this.props.appointmentsMap[
              supervisor.appointment
            ] as IAppointment<ISupervisorAppointmentDetail>;
            if (appointment) {
              guessedStartDate = toDateTime(appointment.finish);
              // guessedFinishDate = new Date(appointment.details.finish)
            }
          }
        }
      }
    }

    this.setState({
      toPersist: {
        person: null,
        start: (guessedStartDate || DateTime.now()).toISO()!,
        finish: (guessedFinishDate || DateTime.now()).toISO()!,
        comment: "",
      },
    });
  }

  getRowAction(
    action: string | Array<string>,
    person: string | null,
    appointment: IAppointment<ISupervisorAppointmentDetail> | undefined,
    rowIndex: number,
    supervisor?: IShiftSupervisor,
  ) {
    if (action instanceof Array)
      return action.map((m) =>
        this.getRowAction(m, person, appointment, rowIndex, supervisor),
      );

    const appointmentIsLocked =
      !!appointment && AppointmentStatus.isLocked(appointment.status);

    const key = `action-${action}-${person}-${rowIndex}`;

    switch (action) {
      case "add":
        return (
          <Button key={key} color="blue" variant="outline" onClick={this.persist}>
            Save
          </Button>
        );
      case "save-edit":
        return (
          <Button
            key={key}
            color="blue"
            variant="outline"
            onClick={this.persistEdit}
          >
            Save
          </Button>
        );
      case "loading":
        return <CenteredLoader key={key} />;
      case "x":
        return person ? (
          <Menu
            key={key}
            opened={this.state.openActionsRow == rowIndex}
            onClose={() => this.toggleDropdown(undefined)}
            onOpen={() => this.toggleDropdown(rowIndex)}
            position="bottom-end"
          >
            <Menu.Target>
              <Button color="gray" variant="subtle">
                <FontAwesomeIcon icon={faEllipsisV} />
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {!appointmentIsLocked && (
                <Menu.Item
                  onClick={() =>
                    this.startEdit(appointment, person, rowIndex, supervisor)
                  }
                  style={{ minHeight: "4vh" }}
                >
                  Edit supervisor
                </Menu.Item>
              )}
              {!appointmentIsLocked && (
                <Menu.Item
                  onClick={() =>
                    this.removeSupervisor(
                      supervisor?.appointment || appointment?._id || null,
                    )
                  }
                  style={{ color: "#dc3545", minHeight: "4vh" }}
                >
                  Remove {this.props.personsMap?.[person]?.firstName}
                </Menu.Item>
              )}

              {appointment && (
                <>
                  {!appointmentIsLocked && (
                    <>
                      <Menu.Item
                        onClick={() => this.toggleAddLoading(appointment?._id)}
                        style={{ minHeight: "4vh" }}
                        disabled={appointmentIsLocked}
                      >
                        Add loading
                      </Menu.Item>

                      <Menu.Item
                        onClick={() =>
                          this.addZeroPaymentLoading(appointment?._id)
                        }
                      >
                        Set payment to $0.00
                      </Menu.Item>
                      {this.props.showHideAppointmentFromSheetButton && <Menu.Item
                        onClick={() => {
                          this.props?.actions?.setPaysheetVisibilityStatus(
                            appointment._id,
                            appointment?.payment?.showTotal === false,
                          );
                        }}
                      >
                        {appointment?.payment?.showTotal === false
                          ? "Show"
                          : "Hide"}{" "}
                        appointment total on paysheet
                      </Menu.Item>}
                    </>
                  )}
                  <Menu.Item
                    onClick={() => this.toggleShowAppointment(appointment?._id)}
                    style={{ minHeight: "4vh" }}
                  >
                    Show details
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Label>
                    {appointmentIsLocked && "Appointment is locked as it was "}
                    {AppointmentStatus.toString(appointment.status)} on{" "}
                    {toDateTimeNullable(
                      appointment.statuses.filter(
                        (s) => s.status == appointment.status,
                      )[0]?.date,
                    )?.toLocaleString(DateTime.DATETIME_FULL)}
                  </Menu.Label>
                  {appointment?.payment && (
                    <Menu.Label>
                      Total: ${Number(appointment.payment.total).toFixed(2)}
                    </Menu.Label>
                  )}
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Button
            key={key}
            variant="outline"
            onClick={() =>
              this.setState((state) => ({ ...state, toPersist: null }))
            }
            color="red"
            size="sm"
          >
            X
          </Button>
        );
      case "cancel-edit":
        return (
          <Button
            key={key}
            variant="outline"
            onClick={this.cancelEdit}
            color="red"
            size="sm"
          >
            X
          </Button>
        );
    }
    return action;
  }

  getRow(
    rowIndex: number,
    person: string | null,
    start: DateTime,
    finish: DateTime,
    mode,
    canModifyPerson: boolean,
    appointment?: IAppointment<ISupervisorAppointmentDetail>,
    modifyHandler?: (name: string, value: any) => void,
    supervisor?: IShiftSupervisor,
  ) {
    const modeAction = this.getRowAction(
      mode,
      person,
      appointment,
      rowIndex,
      supervisor,
    );
    const appointmentIsLocked =
      appointment && AppointmentStatus.isLocked(appointment.status);
    const defaultRowStyle = {
      color: appointmentIsLocked ? "var(--color-text-disabled)" : undefined,
    }
    const onChangeHandler = modifyHandler || this.modifySupervisor;
    const editPersonAction = canModifyPerson ? (
      <OfficialsSelector
        name="person"
        person={person}
        role={Role.supervisor}
        shiftId={this.props.shiftId}
        onChange={onChangeHandler}
      />
    ) : (
      <span
        style={{
          // backgroundColor: appointmentIsLocked ? "#e9ecef" : "",
          ...defaultRowStyle,
          padding: "10px",
        }}
      >
        {this.props.personsMap &&
          person &&
          person in this.props.personsMap &&
          this.props.personsMap[person].fullName}
      </span>
    );

    let minTime: DateTime | null = null;
    let maxTime: DateTime | null = null;

    const range = this.props.session || { start: null, finish: null };
    minTime = range.start && toDateTime(range.start).minus({ minutes: 60 });
    maxTime = range.finish && toDateTime(range.finish).plus({ minutes: 60 });

    const rowKey =
      (appointment && appointment._id) ||
      `${rowIndex}-${person || "unrostered"}-${start.toISO()}`;

    return (
      <tr key={rowKey}>
        <td>{editPersonAction}</td>
        <td style={defaultRowStyle}>
          {canModifyPerson ? (
            <TimeSelector
              name="start"
              time={start}
              minTime={minTime}
              maxTime={finish}
              onChange={onChangeHandler}
              tz={this.venueTz()}
            />
          ) : (
            this.correctedTz(start).toLocaleString(DateTime.TIME_SIMPLE)
          )}
        </td>
        <td style={defaultRowStyle}>
          {canModifyPerson ? (
            <TimeSelector
              name="finish"
              tz={this.venueTz()}
              time={finish}
              minTime={start}
              maxTime={maxTime}
              onChange={onChangeHandler}
            />
          ) : (
            this.correctedTz(finish).toLocaleString(DateTime.TIME_SIMPLE)
          )}
        </td>
        <td>{modeAction}</td>
      </tr>
    );
  }

  getRowFromAppointment(
    rowIndex: number,
    supervisor: IShiftSupervisor,
    mode,
    canModifyPerson: boolean,
  ) {
    const appointment =
      this.props.appointmentsMap &&
      (this.props.appointmentsMap[
        supervisor.appointment
      ] as IAppointment<ISupervisorAppointmentDetail>);
    const s = appointment?.start;
    const f = appointment?.finish;

    if (!s || !f) return <tr><td>Invalid appointment</td></tr>

    return this.getRow(
      rowIndex,
      supervisor.person,
      toDateTime(s),
      toDateTime(f),
      mode,
      canModifyPerson,
      appointment,
      undefined,
      supervisor,
    );
  }

  startEdit(
    appointment: IAppointment<ISupervisorAppointmentDetail> | undefined,
    person: string | null,
    rowIndex: number,
    supervisor?: IShiftSupervisor,
  ) {
    if (!appointment) return;

    const start = toDateTime(appointment.start);
    const finish = toDateTime(appointment.finish);
    const comment =
      (appointment.details &&
        (appointment.details as ISupervisorAppointmentDetail)?.comment) ||
      appointment.comment ||
      "";
    const personId = person ||
      ((appointment.details as ISupervisorAppointmentDetail | undefined)?.person ??
        null);

    this.setState((state) => ({
      ...state,
      editDraft: {
        person: personId,
        start,
        finish,
        comment,
      },
      editingOriginal: {
        person: personId,
        start,
        finish,
        comment,
      },
      editingAppointmentId:
        supervisor?.appointment || appointment._id || null,
      editingRow: rowIndex,
      persistSaving: false,
      toPersist: null,
      openActionsRow: null,
    }));
  }

  cancelEdit() {
    this.setState((state) => ({
      ...state,
      editDraft: null,
      editingOriginal: null,
      editingAppointmentId: null,
      editingRow: null,
      persistSaving: false,
    }));
  }

  modifyEditSupervisor(name: string, value: any) {
    if (!this.state.editDraft) return;

    const dateValue = toDateTimeNullable(value);
    if (dateValue) {
      value = dateValue;
    }

    this.setState((state) => ({
      ...state,
      editDraft: {
        ...state.editDraft!,
        [name]: value,
      },
    }));
  }

  async persistEdit() {
    const { actions, sessionId, venueId } = this.props;
    const { editDraft, editingAppointmentId, editingOriginal } = this.state;

    if (!actions || !editDraft || !editingAppointmentId) {
      return alert("An internal error has ocurred");
    }

    if (!editDraft.person) {
      return alert("Must add person before saving");
    }

    this.setState((state) => ({ ...state, persistSaving: true, error: null }));

    try {
      await actions.removeShiftVenueSupervisor(
        sessionId,
        venueId,
        editingAppointmentId,
      );

      await actions.rosterShiftVenueSupervisor(
        sessionId,
        venueId,
        editDraft.person,
        toDateTime(editDraft.start).toJSDate(),
        toDateTime(editDraft.finish).toJSDate(),
      );

      this.setState((state) => ({
        ...state,
        editDraft: null,
        editingOriginal: null,
        editingAppointmentId: null,
        editingRow: null,
        persistSaving: false,
      }));
    } catch (er) {
      let errorMessage =
        (er && (er as Error).message) || String(er) || "Failed to save supervisor";
      if (editingOriginal?.person) {
        try {
          await actions.rosterShiftVenueSupervisor(
            sessionId,
            venueId,
            editingOriginal.person,
            toDateTime(editingOriginal.start).toJSDate(),
            toDateTime(editingOriginal.finish).toJSDate(),
          );
        } catch (restoreError) {
          const restoreMessage =
            (restoreError && (restoreError as Error).message) ||
            String(restoreError);
          errorMessage += ` (Could not restore original supervisor${restoreMessage ? `: ${restoreMessage}` : ""})`;
        }
      }

      this.setState((state) => ({
        ...state,
        persistSaving: false,
        error: errorMessage,
        editDraft: null,
        editingAppointmentId: null,
        editingOriginal: null,
        editingRow: null,
      }));
    }
  }

  persist() {
    if (!this.state.toPersist || !this.props.actions)
      return alert("An internal error has ocurred");
    if (!this.state.toPersist.person)
      return alert("Must add person before saving");

    this.setState((state) => ({ ...state, persistSaving: true, error: null }));
    this.props.actions
      .rosterShiftVenueSupervisor(
        this.props.sessionId,
        this.props.venueId,
        this.state.toPersist.person,
        toDateTime(this.state.toPersist.start).toJSDate(),
        toDateTime(this.state.toPersist.finish).toJSDate(),
      )
      .then(() =>
        this.setState((state) => ({
          ...state,
          toPersist: null,
          persistSaving: false,
        })),
      )
      .catch((er) =>
        this.setState((state) => ({
          ...state,
          persistSaving: false,
          error: er.message,
        })),
      );
  }

  removeSupervisor(appointmentId: string | null) {
    if (!appointmentId || !this.props.actions) return;
    this.props.actions.removeShiftVenueSupervisor(
      this.props.sessionId,
      this.props.venueId,
      appointmentId,
    ).then;
  }

  modifySupervisor(name: string, value: any) {
    if (!this.state.toPersist) return;
    // @ts-ignore

    const dateValue = toDateTimeNullable(value);
    if (dateValue) {
      value = dateValue;
    }

    // @ts-ignore
    this.setState((state) => ({
      ...state,
      toPersist: { ...this.state.toPersist, [name]: value },
    }));
  }

  render() {
    const { shift, venueId, session } = this.props;
    const { error } = this.state;

    if (!shift) return <p>Couldn't find shift.</p>;
    if (!session) return <p>Couldn't find session</p>;

    const supervisors = session.supervisors || {};
    const venueSupervisors: IShiftSupervisor[] = supervisors[venueId] || [];

    const { editDraft, editingAppointmentId, editingRow } = this.state;
    const supervisorRows: React.ReactElement[] = [];
    let editingRowRendered = false;

    venueSupervisors.forEach((vs, idx) => {
      const appointment =
        this.props.appointmentsMap &&
        (this.props.appointmentsMap[
          vs.appointment
        ] as IAppointment<ISupervisorAppointmentDetail>);

      const isEditing =
        !!editDraft &&
        ((editingAppointmentId && vs.appointment === editingAppointmentId) ||
          (!editingAppointmentId && typeof editingRow === "number" && editingRow === idx));

      if (isEditing && editDraft) {
        supervisorRows.push(
          this.getRow(
            idx,
            editDraft.person,
            toDateTime(editDraft.start),
            toDateTime(editDraft.finish),
            this.state.persistSaving ? "loading" : ["save-edit", "cancel-edit"],
            true,
            appointment,
            this.modifyEditSupervisor,
            vs,
          ),
        );
        editingRowRendered = true;
      } else {
        supervisorRows.push(this.getRowFromAppointment(idx, vs, "x", false));
      }
    });

    if (editDraft && !editingRowRendered) {
      const insertIndex =
        typeof editingRow === "number"
          ? Math.min(editingRow, supervisorRows.length)
          : supervisorRows.length;
      supervisorRows.splice(
        insertIndex,
        0,
        this.getRow(
          insertIndex,
          editDraft.person,
          toDateTime(editDraft.start),
          toDateTime(editDraft.finish),
          this.state.persistSaving ? "loading" : ["save-edit", "cancel-edit"],
          true,
          undefined,
          this.modifyEditSupervisor,
          undefined,
        ),
      );
    }

    let addSupervisorButton: React.ReactElement;
    if (shift.supervisorDefaultTimes) {
      addSupervisorButton = (
        <ButtonGroup>
          <Button variant="outline" onClick={() => this.addSupervisor()}>
            Add supervisor (+)
          </Button>
          <Menu
            opened={this.state.supervisorDefaultTimesMenuOpen || false}
            onClose={() => this.toggleSupervisorDefaultTimesMenu(false)}
            onOpen={() => this.toggleSupervisorDefaultTimesMenu(true)}
            position="bottom-start"
          >
            <Menu.Target>
              <Button variant="outline">▼</Button>
            </Menu.Target>
            <Menu.Dropdown>
              {shift.supervisorDefaultTimes?.map((t) => (
                <Menu.Item
                  key={`${t.start}-${t.finish}`}
                  onClick={() =>
                    this.addSupervisor(
                      toDateTime(t.start),
                      toDateTime(t.finish),
                    )
                  }
                >
                  {toDateTime(t.start).toLocaleString(DateTime.TIME_SIMPLE)} -{" "}
                  {toDateTime(t.finish).toLocaleString(DateTime.TIME_SIMPLE)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </ButtonGroup>
      );
    } else {
      addSupervisorButton = (
        <Button variant="outline" onClick={() => this.addSupervisor()}>
          Add supervisor (+)
        </Button>
      );
    }

    return (
      <>
        <Table style={{ width: "fit-content" }}>
          <thead>
            <tr>
              <td style={{ minWidth: "300px" }}>Supervisor</td>
              <td>Start</td>
              <td>Finish</td>
              <td style={{ minWidth: "fit-content" }}>Mode</td>
            </tr>
          </thead>
          <tbody>
            {supervisorRows}
            {this.state.toPersist && !editDraft &&
              this.getRow(
                supervisorRows.length,
                this.state.toPersist.person,
                toDateTime(this.state.toPersist.start),
                toDateTime(this.state.toPersist.finish),
                this.state.persistSaving
                  ? "loading"
                  : this.state.toPersist.person
                    ? ["add", "x"]
                    : "x",
                true,
                undefined,
                this.modifySupervisor,
                undefined,
              )}
          </tbody>
          { }
        </Table>
        {error && (
          <Alert color="red" toggle={() => this.setState({ error: null })}>
            {error}
          </Alert>
        )}
        {!this.state.toPersist && !editDraft && (
          <div style={{ width: "fit-content", margin: "auto" }}>
            {addSupervisorButton}
          </div>
        )}
        {this.state.showAppointmentDetailsModalAppointment && (
          <Modal
            opened={!!this.state.showAppointmentDetailsModalAppointment}
            onClose={() => this.toggleShowAppointment(null)}
            title="Appointment"
          >
            <AppointmentView
              appointmentId={
                this.state.showAppointmentDetailsModalAppointment
              }
              disableEdits={false}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <Button
                color="gray"
                onClick={() => this.toggleShowAppointment(null)}
              >
                Close
              </Button>
            </div>
          </Modal>
        )}
        {this.state.addLoadingModalAppointment && (
          <Modal
            opened={Boolean(this.state.addLoadingModalAppointment)}
            onClose={() => this.toggleAddLoading(null)}
            title="Add loading"
            size="sm"
          >
            <AddLoadingView
              toggle={() => this.toggleAddLoading(null)}
              appointmentId={this.state.addLoadingModalAppointment}
            />
          </Modal>
        )}
      </>
    );
  }

  toggleDropdown(rowIndex?: number) {
    const newRowIndex = typeof rowIndex === "number" ? rowIndex : null;
    this.setState({ openActionsRow: newRowIndex });
  }

  toggleAddLoading(appointmentId: string | undefined | null) {
    this.setState({ addLoadingModalAppointment: appointmentId });
  }

  toggleShowAppointment(appointmentId: string | undefined | null) {
    this.setState({ showAppointmentDetailsModalAppointment: appointmentId });
  }

  toggleSupervisorDefaultTimesMenu(open: boolean) {
    this.setState({ supervisorDefaultTimesMenuOpen: open });
  }

  addZeroPaymentLoading(appointmentId) {
    if (!appointmentId) {
      return console.log(
        "Couldn't add zero payment loading as an internal error occurred",
      );
    }

    this.props.actions
      ?.addLoading(appointmentId, { reason: BonusType.zeroPayment, value: 0 })
      .then((resp) => {
        console.log(resp);
      })
      .catch((er) => {
        alert(er.message);
        console.error(er);
      });
  }
}

function mapStateToProps(state: IState, props) {
  const shift = state.shifts.map[props.shiftId];

  if (!shift || !props.sessionId) return { shift, session: null };
  const filledShift = state.shiftSessions.filled[props.sessionId];
  return {
    shift,
    session: filledShift && filledShift.session,
    personsMap: state.persons.map,
    appointmentsMap: state.appointments.map,
    venueMap: state.venues.map,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        rosterShiftVenueSupervisor,
        removeShiftVenueSupervisor,
        addLoading,
        setPaysheetVisibilityStatus,
      },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(RosterSupervisor);
